import { useState, useEffect } from "react";
import { getRoles } from "../../../services/admin/roles_admin";

function UserFormModal({ isOpen, onClose, userData, onSubmit }) {
  const isEdit = !!userData;

  const [form, setForm] = useState({
    nombre: "",
    apellido: "",
    correo: "",
    usuario: "",
    password: "",
    estado: 1,
    rol_id: "",
  });

  const [roles, setRoles] = useState([]);

  // ✅ alert del modal
  const [alert, setAlert] = useState(null); // { type: "error"|"info"|"success", message: string }

  useEffect(() => {
    if (!isOpen) return;

    setForm({
      nombre: userData?.nombre || "",
      apellido: userData?.apellido || "",
      correo: userData?.correo || "",
      usuario: userData?.usuario || "",
      password: "",
      estado: userData?.estado ?? 1,
      rol_id: userData?.rol_id || "",
    });

    setAlert(null);
  }, [isOpen, userData]);

  useEffect(() => {
    if (!isOpen) return;

    const loadRoles = async () => {
      try {
        const r = await getRoles();
        setRoles(Array.isArray(r) ? r : []);
      } catch (err) {
        console.error("Error loading roles:", err);
        setRoles([]);
        setAlert({ type: "error", message: getNiceErrorMessage(err, "Could not load roles.") });
      }
    };

    loadRoles();
  }, [isOpen]);

  const updateField = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const getNiceErrorMessage = (error, fallback = "Operation failed.") => {
    // backend: res.status(x).json({ ok:false, message:"..." })
    const msg =
      error?.response?.data?.message ||
      error?.message ||
      fallback;

    // opcional: recortar mensajes larguísimos (stack traces, etc.)
    const clean = String(msg).replace(/\s+/g, " ").trim();
    return clean.length > 200 ? clean.slice(0, 200) + "…" : clean;
  };

  const submit = async (e) => {
    e.preventDefault();
    setAlert(null);

    try {
      const dataToSend = { ...form };

      // ✅ Mantener contraseña actual en edit si viene vacía
      if (isEdit && (!form.password || !form.password.trim())) {
        delete dataToSend.password;
      }

      await onSubmit(dataToSend); // 👈 importante: esperar
      onClose?.();
    } catch (err) {
      console.error("User save failed:", err);
      setAlert({ type: "error", message: getNiceErrorMessage(err, "Could not save user.") });
    }
  };

  if (!isOpen) return null;

  return (
    <div style={ui.overlay}>
      <div style={ui.modal}>

        {/* HEADER */}
        <div style={ui.header}>
          <h2 style={ui.title}>{isEdit ? "Edit User" : "Create User"}</h2>
          <button style={ui.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* ✅ ALERT dentro del modal */}
        {alert && (
          <div style={{ ...ui.alert, ...(alert.type === "error" ? ui.alertError : ui.alertInfo) }}>
            <span style={{ lineHeight: 1.3 }}>{alert.message}</span>
            <button style={ui.alertX} onClick={() => setAlert(null)}>✕</button>
          </div>
        )}

        {/* FORM */}
        <form onSubmit={submit} style={ui.form}>
          <div style={ui.grid}>

            <Field label="First Name">
              <input
                name="nombre"
                value={form.nombre}
                onChange={updateField}
                required
                style={ui.input}
              />
            </Field>

            <Field label="Last Name">
              <input
                name="apellido"
                value={form.apellido}
                onChange={updateField}
                required
                style={ui.input}
              />
            </Field>

            <Field label="Email">
              <input
                type="email"
                name="correo"
                value={form.correo}
                onChange={updateField}
                required
                style={ui.input}
              />
            </Field>

            <Field label="Username">
              <input
                name="usuario"
                value={form.usuario}
                onChange={updateField}
                required
                style={ui.input}
              />
            </Field>

            <Field label="Password">
              <input
                type="password"
                name="password"
                value={form.password}
                onChange={updateField}
                style={ui.input}
                placeholder={isEdit ? "Leave empty to keep current" : ""}
              />
            </Field>

            <Field label="Status">
              <select
                name="estado"
                value={form.estado}
                onChange={updateField}
                style={ui.input}
              >
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </Field>

            <Field label="Role">
              <select
                name="rol_id"
                value={form.rol_id}
                onChange={updateField}
                required
                style={ui.input}
              >
                <option value="">Select role</option>

                {roles.map((r) => (
                  <option key={r.rol_id} value={r.rol_id}>
                    {r.nombre}
                  </option>
                ))}
              </select>
            </Field>

          </div>

          {/* FOOTER */}
          <div style={ui.footer}>
            <button type="button" style={ui.cancelBtn} onClick={onClose}>
              Cancel
            </button>

            <button type="submit" style={ui.saveBtn}>
              {isEdit ? "Save Changes" : "Create User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ---------------- FIELD COMPONENT ---------------- */
function Field({ label, children }) {
  return (
    <div style={ui.field}>
      <label style={ui.label}>{label}</label>
      {children}
    </div>
  );
}

/* ------------------ MODERN STYLES ------------------ */
const ui = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px",
    zIndex: 2000,
  },

  modal: {
    width: "650px",
    background: "#ffffff",
    borderRadius: "14px",
    boxShadow: "0 8px 25px rgba(0,0,0,0.25)",
    overflow: "hidden",
  },

  header: {
    padding: "18px 24px",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  title: {
    fontSize: "20px",
    fontWeight: "600",
    color: "#1f2937",
  },

  closeBtn: {
    background: "none",
    border: "none",
    fontSize: "22px",
    cursor: "pointer",
    color: "#6b7280",
  },

  // ✅ Alert styles
  alert: {
    margin: "14px 22px 0",
    padding: "10px 12px",
    borderRadius: "10px",
    border: "1px solid",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    fontSize: "13px",
    fontWeight: 600,
  },
  alertError: {
    background: "#FEF2F2",
    borderColor: "#FECACA",
    color: "#7F1D1D",
  },
  alertInfo: {
    background: "#EFF6FF",
    borderColor: "#BFDBFE",
    color: "#1E3A8A",
  },
  alertX: {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: "16px",
    opacity: 0.7,
  },

  form: {
    padding: "24px",
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "18px",
  },

  field: {
    display: "flex",
    flexDirection: "column",
  },

  label: {
    fontSize: "13px",
    color: "#374151",
    marginBottom: "6px",
    fontWeight: 500,
  },

  input: {
    padding: "10px 12px",
    borderRadius: "8px",
    border: "1px solid #D1D5DB",
    background: "#F8FAFC",
    color: "#1F2937",
    fontSize: "15px",
    outline: "none",
  },

  footer: {
    marginTop: "24px",
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    borderTop: "1px solid #e5e7eb",
    paddingTop: "14px",
  },

  cancelBtn: {
    padding: "10px 18px",
    background: "#E5E7EB",
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    fontWeight: 500,
  },

  saveBtn: {
    padding: "10px 18px",
    background: "#2563EB",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: 600,
  },
};

export default UserFormModal;
